import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Package, Truck, CheckCircle, Clock, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { getPriceData } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMNS = [
    { id: 'pending', title: 'Нови', icon: Clock, color: 'bg-yellow-500', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20' },
    { id: 'awaiting_prepayment', title: 'Очаква плащане', icon: Clock, color: 'bg-orange-500', bgColor: 'bg-orange-50 dark:bg-orange-900/20' },
    { id: 'confirmed', title: 'Потвърдени', icon: Package, color: 'bg-blue-500', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
    { id: 'processing', title: 'Обработват се', icon: Package, color: 'bg-purple-500', bgColor: 'bg-purple-50 dark:bg-purple-900/20' },
    { id: 'shipped', title: 'Изпратени', icon: Truck, color: 'bg-amber-500', bgColor: 'bg-amber-50 dark:bg-amber-900/20' },
    { id: 'delivered', title: 'Доставени', icon: CheckCircle, color: 'bg-green-500', bgColor: 'bg-green-50 dark:bg-green-900/20' },
    { id: 'cancelled', title: 'Отказани', icon: XCircle, color: 'bg-red-500', bgColor: 'bg-red-50 dark:bg-red-900/20' }
];

const paymentLabels = {
    'cod': 'Наложен платеж',
    'card': 'С карта',
    'bank_transfer': 'Банков превод'
};

const OrderCard = ({ order, index, onOrderClick }) => {
    return (
        <Draggable draggableId={order.order_id} index={index} isDragDisabled={false}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`bg-white dark:bg-slate-800 rounded-lg border border-border dark:border-slate-700 p-3 mb-2 cursor-grab transition-shadow
                        ${snapshot.isDragging ? 'shadow-lg ring-2 ring-amber-400' : 'hover:shadow-md'}`}
                    onClick={() => onOrderClick(order)}
                    data-testid={`kanban-order-${order.order_id}`}
                >
                    <div className="flex justify-between items-start mb-2">
                        <span className="font-mono text-sm font-semibold text-slate-700 dark:text-slate-300">
                            #{order.order_id.slice(-8).toUpperCase()}
                        </span>
                        <div className="text-right">
                            <span className="font-bold text-amber-600 dark:text-amber-400">
                                {getPriceData(order.total).eurFormatted}€
                            </span>
                        </div>
                    </div>
                    
                    <p className="text-sm text-slate-600 dark:text-slate-400 truncate mb-2">
                        {order.address?.name || 'Без име'}
                    </p>
                    
                    {/* Customer Trust Indicator */}
                    {order.customer_info && (
                        <div className="mb-2">
                            {order.customer_info.is_new ? (
                                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                    🆕 Нов
                                </span>
                            ) : order.customer_info.is_trusted ? (
                                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                                    ⭐ Доверен
                                </span>
                            ) : (
                                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">
                                    ⚠️ Внимание
                                </span>
                            )}
                        </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">
                            {paymentLabels[order.payment_method] || order.payment_method}
                        </Badge>
                        <span className="text-xs text-slate-500 dark:text-slate-500">
                            {order.items?.length || 0} продукта
                        </span>
                    </div>
                    
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                        {new Date(order.created_at).toLocaleDateString('bg-BG', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                    </p>
                </div>
            )}
        </Draggable>
    );
};

const KanbanColumn = ({ column, orders, onOrderClick }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const Icon = column.icon;
    
    return (
        <div className={`flex flex-col min-w-[280px] max-w-[320px] rounded-xl ${column.bgColor} border border-slate-200 dark:border-slate-700`}>
            <div 
                className="p-3 flex items-center justify-between cursor-pointer"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg ${column.color} flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-700 dark:text-slate-200">{column.title}</h3>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{orders.length} поръчки</span>
                    </div>
                </div>
                {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </div>
            
            {!isCollapsed && (
                <Droppable droppableId={column.id} isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`flex-1 p-2 min-h-[200px] max-h-[calc(100vh-300px)] overflow-y-auto transition-colors
                                ${snapshot.isDraggingOver ? 'bg-amber-100/50 dark:bg-amber-900/30' : ''}`}
                        >
                            {orders.map((order, index) => (
                                <OrderCard 
                                    key={order.order_id} 
                                    order={order} 
                                    index={index} 
                                    onOrderClick={onOrderClick}
                                />
                            ))}
                            {provided.placeholder}
                            {orders.length === 0 && (
                                <p className="text-center text-sm text-slate-400 dark:text-slate-500 py-8">
                                    Няма поръчки
                                </p>
                            )}
                        </div>
                    )}
                </Droppable>
            )}
        </div>
    );
};

const OrderDetailModal = ({ order, onClose, onStatusChange }) => {
    if (!order) return null;
    
    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div 
                className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h2 className="text-xl font-bold dark:text-white">Поръчка #{order.order_id}</h2>
                        <p className="text-sm text-slate-500">
                            {new Date(order.created_at).toLocaleDateString('bg-BG', {
                                year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                        </p>
                    </div>
                    <Button variant="ghost" onClick={onClose}>✕</Button>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-3">
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Клиент</p>
                        <p className="font-medium dark:text-white">{order.address?.name}</p>
                        <p className="text-sm dark:text-slate-300">{order.address?.phone}</p>
                        {(order.user_email || order.address?.email) ? (
                            <>
                                <p className="text-sm dark:text-slate-300 break-all">{order.user_email || order.address?.email}</p>
                                {order.email_log?.confirmation && (
                                    <p className={`text-xs mt-1 ${order.email_log.confirmation.sent ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                        {order.email_log.confirmation.sent ? '✓ Имейл потвърждение изпратено' : '✗ Имейлът не бе изпратен'}
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className="text-xs italic text-slate-400 mt-1">Без имейл — няма имейл известия</p>
                        )}
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-3">
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Адрес за доставка</p>
                        <p className="text-sm dark:text-slate-300">{order.address?.address}</p>
                        <p className="text-sm dark:text-slate-300">{order.address?.city}</p>
                    </div>
                </div>
                
                <div className="border-t dark:border-slate-700 pt-4 mb-4">
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Продукти</p>
                    <div className="space-y-2">
                        {order.items?.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3 bg-slate-50 dark:bg-slate-700 rounded-lg p-2">
                                <img src={item.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                                <div className="flex-1">
                                    <p className="font-medium dark:text-white">{item.name}</p>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                        {item.quantity} x {getPriceData(item.price || 0).eurFormatted}€
                                    </p>
                                </div>
                                <div className="text-right">
                                    <span className="font-semibold dark:text-white">
                                        {getPriceData(item.quantity * (item.price || 0)).eurFormatted}€
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                <div className="flex justify-between items-center border-t dark:border-slate-700 pt-4">
                    <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Общо</p>
                        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{getPriceData(order.total || 0).eurFormatted}€</p>
                    </div>
                    <div className="flex gap-2">
                        <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                            {order.payment_status === 'paid' ? 'Платено' : order.payment_status === 'cod' ? 'При доставка' : 'Чакащо'}
                        </Badge>
                    </div>
                </div>
                
                {order.econt_shipment && (
                    <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                            <p className="text-sm font-medium text-green-700 dark:text-green-400">
                                Товарителница
                            </p>
                        </div>
                        <p className="font-mono text-sm mt-1 text-green-800 dark:text-green-300">
                            {order.econt_shipment.shipment_number}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

const KanbanOrdersBoard = () => {
    const { token } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const response = await axios.get(`${API}/admin/orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(response.data.orders || []);
        } catch (error) {
            console.error('Error fetching orders:', error);
            toast.error('Грешка при зареждане на поръчките');
        } finally {
            setLoading(false);
        }
    };

    const getOrdersByStatus = (status) => {
        return orders.filter(order => order.status === status);
    };

    const handleDragEnd = async (result) => {
        const { destination, source, draggableId } = result;

        // Dropped outside or same position
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }

        const newStatus = destination.droppableId;
        const orderId = draggableId;

        // Optimistic update
        const updatedOrders = orders.map(order => 
            order.order_id === orderId ? { ...order, status: newStatus } : order
        );
        setOrders(updatedOrders);

        try {
            await axios.put(`${API}/admin/orders/${orderId}/status`, 
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Статусът е променен на "${COLUMNS.find(c => c.id === newStatus)?.title}"`);
        } catch (error) {
            // Revert on error
            setOrders(orders);
            toast.error('Грешка при промяна на статуса');
        }
    };

    if (loading) {
        return (
            <div className="flex gap-4 overflow-x-auto pb-4">
                {COLUMNS.map(col => (
                    <div key={col.id} className="min-w-[280px] h-96 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />
                ))}
            </div>
        );
    }

    const syncEcontStatuses = async () => {
        try {
            toast.info('Синхронизиране на Еконт статуси...');
            const response = await axios.post(`${API}/admin/econt/sync-all`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const results = response.data.results;
            if (results.updated > 0) {
                toast.success(`Обновени ${results.updated} поръчки (${results.delivered} доставени, ${results.shipped} изпратени)`);
                fetchOrders(); // Refresh orders
            } else {
                toast.info('Няма промени в статусите');
            }
        } catch (error) {
            toast.error('Грешка при синхронизиране');
        }
    };

    return (
        <div data-testid="kanban-orders-board">
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-serif text-2xl font-bold dark:text-white">Kanban Поръчки</h1>
                <div className="flex gap-2">
                    <Button 
                        variant="outline" 
                        onClick={syncEcontStatuses} 
                        className="dark:border-slate-600 text-amber-600 border-amber-300 hover:bg-amber-50"
                        data-testid="sync-econt-btn"
                    >
                        🚚 Синхронизирай Еконт
                    </Button>
                    <Button variant="outline" onClick={fetchOrders} className="dark:border-slate-600">
                        🔄 Обнови
                    </Button>
                </div>
            </div>
            
            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="flex gap-4 overflow-x-auto pb-4">
                    {COLUMNS.map(column => (
                        <KanbanColumn
                            key={column.id}
                            column={column}
                            orders={getOrdersByStatus(column.id)}
                            onOrderClick={setSelectedOrder}
                        />
                    ))}
                </div>
            </DragDropContext>
            
            <OrderDetailModal 
                order={selectedOrder} 
                onClose={() => setSelectedOrder(null)}
            />
        </div>
    );
};

export default KanbanOrdersBoard;
